import { supportsValidationTargetVersion } from './validation-version-scope.js'
import { validationConfiguration, readValidationCertificate, VALIDATION_STAMP_TYPE, VALIDATION_RESULT_FORM } from './validation-stamps.js'
import { resolveValidationRequirements, type ValidationRequirements } from './validation-requirements.js'
import * as Types from '@aeppic/types'

import { Actions } from './actions.js'
import { EditableDocument, getDocumentId, isReference } from '../model/index.js'
import type { FieldInfo } from '../model/form.js'
import type { AeppicInterface } from './aeppic.js'
import type { ActionResult } from '../dynamic/run-action.js'
import type { Logger } from '../model/log.js'

export type ValidationStatus = 'valid' | 'invalid' | 'warning' | 'not_applicable'

export const VALIDATION_CATEGORIES = {
  BUSINESS_RULES: 'BUSINESS_RULES',
  DIALOG_SAVE: 'DIALOG_SAVE',
  COMMAND_TARGET: 'COMMAND_TARGET',
  WORKFLOW_TRANSITION: 'WORKFLOW_TRANSITION',
  STAMP: 'STAMP',
} as const

export type ValidationCategory = typeof VALIDATION_CATEGORIES[keyof typeof VALIDATION_CATEGORIES]

export type ValidationMessage = {
  message: string
  code?: string
  [key: string]: any
}

export type ValidationEvidence = {
  label?: string
  code?: string
} & (
  | {
      type: 'data'
      f: Types.Reference
      data: object
    }
  | {
      type: 'document'
      document: Types.Reference | Types.Document
    }
)

export type ValidationResult = {
  valid: boolean
  status: ValidationStatus
  warnings: ValidationMessage[]
  errors: ValidationMessage[]
  evidence: ValidationEvidence[]
  fallback?: 'BASIC'
  actionResults?: ActionResult[]
}

export type BasicValidationResult = {
  valid: boolean
  errors: any
}

export type FieldValidationResult = ValidationResult & {
  fields: Array<{
    fieldName: string
    validations: string[]
    result: ValidationResult
  }>
}

export type ValidationSetResult = {
  set: Types.Document
  validations: Array<{
    rule: Types.Reference | Types.Document
    result: ValidationResult
  }>
  result: ValidationResult
}

export type TargetValidationResult = ValidationResult & {
  basic?: BasicValidationResult
  fieldValidations?: FieldValidationResult
  sets: ValidationSetResult[]
}

export type PreconditionResult = {
  passed: boolean
  steps: {
    formSide?: { valid: boolean, category: string, setsRun: number }
    callerSide?: { valid: boolean, failedName?: string }
    _coveredNames?: Set<string>
  }
}

export type ValidationContext = {
  target: Types.Document
  document?: Types.Document
  rule: Types.Document
  purpose: string
  params: object
  validateBasic: () => Promise<ValidationResult>
}

export type ValidationRunOptions = {
  purpose?: string
  noIsolation?: boolean
  logLimit?: number
  log?: Logger
  timeoutMs?: number
  /** Only replacement BASIC validators may request exact-version BASIC fallback. */
  fallbackToBasic?: boolean
  requireBasicValidation?: boolean
}

type ValidationRule = Types.Document & {
  data: {
    actions?: Types.ReferenceField[]
    action?: Types.Reference
    purpose?: string
    [key: string]: any
  }
}

type ValidationSet = Types.Document & {
  data: {
    categories?: string[]
    validations?: Types.ReferenceField[]
    purpose?: string
    [key: string]: any
  }
}

export class ValidationResultBuilder {
  private _valid = true
  private _warnings: ValidationMessage[] = []
  private _errors: ValidationMessage[] = []
  private _evidence: ValidationEvidence[] = []

  private _notApplicable = false

  notApplicable() {
    this._notApplicable = true
    return this
  }

  valid() {
    return this
  }

  invalid(...args: any[]) {
    this._valid = false
    if (args.length > 0) {
      this._errors.push(toMessage(args))
    }
    return this
  }

  warning(...args: any[]) {
    this._warnings.push(toMessage(args))
    return this
  }

  evidence(formRef: Types.Reference, data: object): this
  evidence(evidence: ValidationEvidence | { f: Types.Reference, data: object, label?: string, code?: string } | { document: Types.Reference | Types.Document, label?: string, code?: string }): this
  evidence(arg1: any, arg2?: object) {
    if (arg2 !== undefined) {
      this._evidence.push({ type: 'data', f: arg1, data: arg2 })
      return this
    }

    if (arg1?.document) {
      this._evidence.push({
        type: 'document',
        document: arg1.document,
        ...(arg1.label ? { label: arg1.label } : {}),
        ...(arg1.code ? { code: arg1.code } : {}),
      })
      return this
    }

    if (arg1?.f && arg1?.data) {
      this._evidence.push({
        type: 'data',
        f: arg1.f,
        data: arg1.data,
        ...(arg1.label ? { label: arg1.label } : {}),
        ...(arg1.code ? { code: arg1.code } : {}),
      })
      return this
    }

    throw new Error('Invalid validation evidence')
  }

  toJSON(): ValidationResult {
    let status: ValidationStatus = 'valid'
    if (!this._valid || this._errors.length > 0) {
      status = 'invalid'
    } else if (this._notApplicable) {
      status = 'not_applicable'
    } else if (this._warnings.length > 0) {
      status = 'warning'
    }

    return {
      valid: status === 'valid' || status === 'warning',
      status,
      warnings: [...this._warnings],
      errors: [...this._errors],
      evidence: [...this._evidence],
    }
  }
}

const validationQueues = new WeakMap<object, Map<string, Promise<any>>>()

export class Validations {
  private _actions: Actions

  constructor(private _aeppic: AeppicInterface) {
    this._actions = (_aeppic as any).Actions || new Actions(_aeppic)
    Object.freeze(this)
  }

  async run(ruleIdentifier: Types.IdOrReference | Types.Document, targetIdentifier: Types.IdOrReference | Types.Document, params: object = {}, options: ValidationRunOptions = {}): Promise<ValidationResult> {
    const rule = await this._getRule(ruleIdentifier)

    if (!rule) {
      throw new Error('Unknown validation rule or rule not accessible')
    }

    const target = await this._getTarget(targetIdentifier)

    if (!target) {
      throw new Error(`Unknown validation target or target not accessible (${JSON.stringify(targetIdentifier)})`)
    }

    if (!await supportsValidationTargetVersion(this._aeppic, rule, target)) {
      return this._resolveVersionFallback({
        valid: false, status: 'not_applicable', warnings: [], errors: [], evidence: [],
      }, target, options)
    }

    const actionRefs = getActionRefs(rule)
    const resultBuilder = new ValidationResultBuilder()
    const context: ValidationContext = {
      target,
      document: target,
      rule,
      purpose: options.purpose ?? (typeof rule.data?.purpose === 'string' ? rule.data.purpose : ''),
      params: params || {},
      validateBasic: () => this._basicValidationResult(target),
    }

    const strict = rule.data?.actionContract === 'STRICT_CHAIN'
    if (actionRefs.length === 0) {
      if (strict) resultBuilder.invalid({ code: 'VALIDATION_EMPTY_CHAIN', message: 'Strict validation requires at least one action.' })
      return resultBuilder.toJSON()
    }

    const actionOptions = {
      aeppic: new ReadOnlyValidationAeppic(this._aeppic) as any,
      log: options.log,
      globals: { Result: resultBuilder },
      timeoutMs: options.timeoutMs,
    }
    const actionResults: ActionResult[] = []
    if (strict) {
      for (let index = 0; index < actionRefs.length; index++) {
        // Resolve individually: getAll implementations may omit missing references.
        const action = await this._aeppic.get(actionRefs[index])
        if (!action) {
          resultBuilder.invalid({ code: 'VALIDATION_ACTION_MISSING', message: 'Validation action could not be resolved.', action: actionRefs[index] })
          break
        }
        const results = await this._actions.run([action], context, actionOptions)
        actionResults.push(...results)
        const result = results[0]
        if (!result || !result.ok) {
          resultBuilder.invalid({ code: 'VALIDATION_ACTION_ERROR', message: result?.error || 'Validation action failed.' })
          break
        }
        if (isNotApplicableResult(result.value)) {
          resultBuilder.notApplicable()
          break
        }
        if (isValidationResultLike(result.value)) {
          mergeResult(resultBuilder, result.value)
          if (!resultBuilder.toJSON().valid) break
          continue
        }
        if (result.value === false) {
          resultBuilder.invalid({ code: 'VALIDATION_FAILED', message: rule.data.failureMessage || 'Validation requirements were not met.' })
          break
        }
        if (isDocument(result.value) && index + 1 < actionRefs.length) {
          context.document = result.value
        } else if (result.value !== true) {
          resultBuilder.invalid({ code: 'VALIDATION_RETURN_INVALID', message: 'Return true, false, or a document followed by another action.' })
          break
        }
        if (!resultBuilder.toJSON().valid) break
      }
    } else {
      actionResults.push(...await this._actions.run(actionRefs, context, actionOptions))
      for (const actionResult of actionResults) applyActionResult(actionResult, resultBuilder)
    }

    return this._resolveVersionFallback({
      ...resultBuilder.toJSON(),
      actionResults: options.logLimit === 0 ? undefined : actionResults,
    }, target, options)
  }

  private async _resolveVersionFallback(result: ValidationResult, target: Types.Document, options: ValidationRunOptions): Promise<ValidationResult> {
    if (result.status !== 'not_applicable' || options.fallbackToBasic !== true) return result
    const basic = await this._basicValidationResult(target)
    return { ...basic, fallback: 'BASIC' }
  }

  private async _basicValidationResult(target: Types.Document): Promise<ValidationResult> {
    const basic = await this.validateBasic(target)
    return {
      valid: basic.valid,
      status: basic.valid ? 'valid' : 'invalid',
      warnings: [], evidence: [],
      errors: basic.valid ? [] : [{ code: 'BASIC_VALIDATION_FAILED', message: 'Basic validation failed', errors: basic.errors }],
    }
  }

  async validateBasic(targetIdentifier: Types.IdOrReference | Types.Document): Promise<BasicValidationResult> {
    const target = await this._getTarget(targetIdentifier)

    if (!target) {
      throw new Error(`Unknown validation target or target not accessible (${JSON.stringify(targetIdentifier)})`)
    }

    const form = await (this._aeppic as any).getFormForDocument(target)
    if (!form) throw new Error('The exact document form version is unavailable')
    const data = copyValidationData(target.data)
    const errors = form.validateDocumentData(data)

    return {
      valid: errors == null,
      errors,
    }
  }

  async validateFields(targetIdentifier: Types.IdOrReference | Types.Document): Promise<FieldValidationResult> {
    const target = await this._getTarget(targetIdentifier)

    if (!target) {
      throw new Error(`Unknown validation target or target not accessible (${JSON.stringify(targetIdentifier)})`)
    }

    const form = await (this._aeppic as any).getFormForDocument(target)
    const declarations = typeof form?.getAllFieldValidations === 'function'
      ? form.getAllFieldValidations()
      : []
    const builder = new ValidationResultBuilder()
    const fields: FieldValidationResult['fields'] = []

    for (const declaration of declarations) {
      const fieldBuilder = new ValidationResultBuilder()

      for (const validation of declaration.validations) {
        await applyBuiltInFieldValidation(fieldBuilder, validation, {
          aeppic: this._aeppic,
          target,
          field: declaration.field,
          fieldName: declaration.fieldName,
          value: target.data?.[declaration.fieldName],
        })
      }

      const result = fieldBuilder.toJSON()
      fields.push({
        fieldName: declaration.fieldName,
        validations: declaration.validations,
        result,
      })
      mergeResult(builder, result)
    }

    return {
      ...builder.toJSON(),
      fields,
    }
  }

  async checkTargetValidationRequirements(targetIdentifier: Types.IdOrReference | Types.Document, category: string, consumer: any = {}) {
    const enabled = (this._aeppic as any).Features?.isEnabled('validation-preconditions') === true
    const policyFields = ['validationMode', 'runWhenBasicValidationFails', 'skipValidation',
      'requireValidations', 'validationRequire', 'validationExclude']
    const hasValidationPolicy = policyFields.some(field =>
      Object.prototype.hasOwnProperty.call(consumer, field))

    // Old consumer documents stay permissive until upgraded to a policy-bearing schema.
    if (!enabled || !hasValidationPolicy) {
      return { status: 'NOT_REQUIRED', passed: true, checks: [], errors: [] }
    }

    const selectors = async (values: any) => Promise.all((Array.isArray(values) ? values : []).map(async value => {
      if (value.kind) return value
      const document = await this._aeppic.get(value)
      return document?.data || { kind: 'KEY', value: 'UNRESOLVED:' + value.id }
    }))
    const requirements: ValidationRequirements = {
      mode: consumer.skipValidation === true ? 'BYPASS' : consumer.validationMode || 'INHERIT',
      require: [...await selectors(consumer.validationRequire), ...(Array.isArray(consumer.requireValidations)
        ? consumer.requireValidations.map((value: string) => ({ kind: 'KEY', value: value === 'basic' ? 'aeppic:basic' : value })) : [])],
      exclude: [...await selectors(consumer.validationExclude), ...(consumer.runWhenBasicValidationFails
        ? [{ kind: 'KEY' as const, value: 'aeppic:basic' }] : [])],
    }
    if (consumer.skipValidationSets === true && requirements.mode !== 'BYPASS') {
      requirements.mode = 'REQUIRE'
    }
    const target = await this._getTarget(targetIdentifier)
    if (!target) throw new Error('Unknown validation target')
    const resolved = await this.resolveRequirements(target, category, requirements, enabled)
    const result = await this.check(target, requirements, category, enabled)
    // Legacy inline rules retain their execution semantics. Async rules require stamps.
    for (const check of result.checks) {
      if (check.rule && check.rule.data?.executionMode !== 'ASYNC_STAMPED') {
        check.result = await this.run(check.rule, target, {}, {
          fallbackToBasic: resolved.basicReplacementRuleIds.includes(check.rule.id),
        })
        check.status = check.result.valid ? 'PASSED' : 'FAILED'
      }
    }
    result.passed = !resolved.errors.length && result.checks.every(check => check.status === 'PASSED')
    result.status = resolved.errors.length || result.checks.some(c => c.status === 'ERROR') ? 'ERROR'
      : result.checks.some(c => c.status === 'FAILED') ? 'INVALID'
      : result.checks.some(c => c.status === 'PENDING') ? 'PENDING'
      : result.checks.length ? 'VALID' : 'NOT_REQUIRED'
    return result
  }

  /** @deprecated Use checkTargetValidationRequirements instead. */
  async checkConsumer(targetIdentifier: Types.IdOrReference | Types.Document, category: string, consumer: any = {}) {
    return this.checkTargetValidationRequirements(targetIdentifier, category, consumer)
  }

  /** Background entry point: checks all explicitly opted-in async rules, regardless of blocking flag. */
  async validateAsync(targetIdentifier: Types.IdOrReference | Types.Document, force = false) {
    const target = await this._getTarget(targetIdentifier)
    if (!target) throw new Error('Unknown validation target')
    const initial = await this.resolveRequirements(target, '', { mode: 'REQUIRE' }, false)
    const ids = new Set<string>((initial.form.data?.validationRules as any[] || []).map(ref => ref.id))
    for (const ref of initial.form.data?.validationSets as any[] || []) {
      const set = await this._aeppic.get(ref)
      if (set?.data?.enabled === false) continue
      for (const rule of set?.data?.validations as any[] || []) ids.add(rule.id)
    }
    const results = []
    for (const id of ids) {
      const rule = await this._getRule(id)
      if (!rule || rule.data?.enabled === false || rule.data?.disabled === true || rule.data?.executionMode !== 'ASYNC_STAMPED') continue
      const configuration = await validationConfiguration(this._aeppic, rule, initial.form)
      if (!force && await readValidationCertificate(this._aeppic, target, rule, configuration)) continue
      results.push(await this.runStamped(rule, target))
    }
    return results
  }

  /** Read-only: never executes validation actions or publishes stamps. */
  async check(targetIdentifier: Types.IdOrReference | Types.Document, requirements: ValidationRequirements = {}, category = 'COMMAND_TARGET', defaultBlocking = true) {
    const target = await this._getTarget(targetIdentifier)
    if (!target) throw new Error('Unknown validation target')
    const resolved = await this.resolveRequirements(target, category, requirements, defaultBlocking)
    const checks: any[] = []
    if (resolved.basic) {
      const result = await this.validateBasic(target)
      checks.push({ key: 'aeppic:basic', status: result.valid ? 'PASSED' : 'FAILED', result })
    }
    for (const rule of resolved.rules) {
      try {
        const configuration = await validationConfiguration(this._aeppic, rule, resolved.form)
        const current = await readValidationCertificate(this._aeppic, target, rule, configuration)
        checks.push({ rule, status: current?.certificate.outcome || 'PENDING', result: current?.result })
      } catch (error) {
        checks.push({ rule, status: 'ERROR', message: error.message })
      }
    }
    const status = resolved.errors.length || checks.some(c => c.status === 'ERROR') ? 'ERROR'
      : checks.some(c => c.status === 'FAILED') ? 'INVALID'
      : checks.some(c => c.status === 'PENDING') ? 'PENDING'
      : checks.length ? 'VALID' : 'NOT_REQUIRED'
    return { status, passed: status === 'VALID' || status === 'NOT_REQUIRED', checks, errors: resolved.errors }
  }

  async isValid(target: Types.IdOrReference | Types.Document, requirements: ValidationRequirements = {}) {
    return (await this.check(target, requirements)).status === 'VALID'
  }

  /** Explicit execution. Callers schedule this outside read-only/UI status checks. */
  async runStamped(ruleIdentifier: Types.IdOrReference | Types.Document, targetIdentifier: Types.IdOrReference | Types.Document) {
    let queues = validationQueues.get(this._aeppic)
    if (!queues) { queues = new Map(); validationQueues.set(this._aeppic, queues) }
    const key = getDocumentId(targetIdentifier as any) + ':' + getDocumentId(ruleIdentifier as any)
    const previous = queues.get(key) || Promise.resolve()
    const next = previous.catch(() => {}).then(() => this._runStamped(ruleIdentifier, targetIdentifier))
    queues.set(key, next)
    try { return await next } finally { if (queues.get(key) === next) queues.delete(key) }
  }

  private async _runStamped(ruleIdentifier: Types.IdOrReference | Types.Document, targetIdentifier: Types.IdOrReference | Types.Document) {
    const target = await this._getTarget(targetIdentifier)
    const rule = await this._getRule(ruleIdentifier)
    if (!target || !rule) throw new Error('Unknown validation target or rule')
    const resolved = await this.resolveRequirements(target, '', { mode: 'REQUIRE', require: [{ kind: 'RULE', value: rule.id }] }, false)
    if (resolved.errors.length) throw new Error(resolved.errors.join('; '))
    const configuration = await validationConfiguration(this._aeppic, rule, resolved.form)
    let result: ValidationResult
    try { result = await this.run(rule, target, {}, {
      fallbackToBasic: resolved.basicReplacementRuleIds.includes(rule.id),
    }) }
    catch (error) {
      result = { valid: false, status: 'invalid', warnings: [], evidence: [], errors: [{ code: 'VALIDATION_ACTION_ERROR', message: error.message }] }
    }
    const latest = await this._aeppic.get(target.id)
    const latestRule = await this._aeppic.get(rule.id)
    const latestResolved = await this.resolveRequirements(latest, '', { mode: 'REQUIRE', require: [{ kind: 'RULE', value: rule.id }] }, false)
    if (!latestRule || latest.v !== target.v || latestResolved.errors.length
      || await validationConfiguration(this._aeppic, latestRule, latestResolved.form) !== configuration) {
      return { published: false, result, reason: 'STALE_INPUT' }
    }
    const form = await this._aeppic.get(VALIDATION_RESULT_FORM)
    if (!form) throw new Error('Validation result form is not installed')
    const outcome = result.errors.some(e => ['VALIDATION_ACTION_ERROR', 'VALIDATION_ACTION_MISSING', 'VALIDATION_RETURN_INVALID', 'VALIDATION_EMPTY_CHAIN'].includes(e.code || ''))
      || result.actionResults?.some(r => !r.ok) ? 'ERROR' : result.valid ? 'PASSED' : 'FAILED'
    const { actionResults, ...storedResult } = result
    await (this._aeppic as any).stamp(target, VALIDATION_STAMP_TYPE, {
      workflowUid: 'validation:' + rule.id,
      additionalDataDocument: { f: { id: form.id, v: form.v, text: form.data?.name || '' }, data: {
        ruleId: rule.id, ruleKey: rule.data?.key || '', configuration,
        checkedAt: new Date().toISOString(), outcome, resultJson: JSON.stringify(storedResult),
      } },
    })
    return { published: true, result, outcome }
  }

  async resolveRequirements(target: Types.Document, category: string, requirements: ValidationRequirements = {}, defaultBlocking = true) {
    return resolveValidationRequirements(this._aeppic, target, category, requirements, defaultBlocking)
  }

  async getSetsForTarget(targetIdentifier: Types.IdOrReference | Types.Document, category: ValidationCategory | string): Promise<ValidationSet[]> {
    const target = await this._getTarget(targetIdentifier)

    if (!target) {
      throw new Error(`Unknown validation target or target not accessible (${JSON.stringify(targetIdentifier)})`)
    }

    const form = await (this._aeppic as any).getFormForDocument(target)
    const validationSetRefs = form?.document?.data?.validationSets

    if (!Array.isArray(validationSetRefs) || validationSetRefs.length === 0) {
      return []
    }

    const sets = await this._aeppic.getAll(validationSetRefs) as ValidationSet[]
    return sets.filter(set => Array.isArray(set?.data?.categories) && set.data.categories.includes(category))
  }

  async runForTarget(targetIdentifier: Types.IdOrReference | Types.Document, category: ValidationCategory | string, params: object = {}, options: ValidationRunOptions = {}): Promise<TargetValidationResult> {
    const target = await this._getTarget(targetIdentifier)

    if (!target) {
      throw new Error(`Unknown validation target or target not accessible (${JSON.stringify(targetIdentifier)})`)
    }

    const builder = new ValidationResultBuilder()
    let basic: BasicValidationResult | undefined
    let fieldValidations: FieldValidationResult | undefined

    if (options.requireBasicValidation === true) {
      basic = await this.validateBasic(target)

      if (!basic.valid) {
        builder.invalid({
          code: 'BASIC_VALIDATION_FAILED',
          message: 'Basic validation failed',
          errors: basic.errors,
        })
      }
    }

    fieldValidations = await this.validateFields(target)
    mergeResult(builder, fieldValidations)

    const sets = await this.getSetsForTarget(target, category)
    const setResults: ValidationSetResult[] = []

    for (const set of sets) {
      const setBuilder = new ValidationResultBuilder()
      const validations = Array.isArray(set.data?.validations) ? set.data.validations : []
      const validationResults: ValidationSetResult['validations'] = []
      const purpose = typeof set.data?.purpose === 'string' && set.data.purpose.length > 0
        ? set.data.purpose
        : category

      for (const ruleRef of validations) {
        const result = await this.run(ruleRef, target, params, { ...options, purpose })
        validationResults.push({ rule: ruleRef, result })
        mergeResult(setBuilder, result)
        mergeResult(builder, result)
      }

      setResults.push({
        set,
        validations: validationResults,
        result: setBuilder.toJSON(),
      })
    }

    return {
      ...builder.toJSON(),
      basic,
      fieldValidations,
      sets: setResults,
    }
  }

  async getSetsForTargetByName(targetIdentifier: Types.IdOrReference | Types.Document, name: string): Promise<ValidationSet[]> {
    const target = await this._getTarget(targetIdentifier)

    if (!target) {
      throw new Error(`Unknown validation target or target not accessible (${JSON.stringify(targetIdentifier)})`)
    }

    const form = await (this._aeppic as any).getFormForDocument(target)
    const validationSetRefs = form?.document?.data?.validationSets

    if (!Array.isArray(validationSetRefs) || validationSetRefs.length === 0) {
      return []
    }

    const sets = await this._aeppic.getAll(validationSetRefs) as ValidationSet[]
    const lowerName = name.toLowerCase()
    return sets.filter(set => typeof set?.data?.name === 'string' && set.data.name.toLowerCase() === lowerName)
  }

  async checkPreconditions(
    target: Types.Document,
    category: ValidationCategory | string,
    callerRequiredNames?: string[] | false,
    options: { skipValidation?: boolean } = {}
  ): Promise<PreconditionResult> {
    // Step 1: Global opt-out
    if (options.skipValidation === true) {
      return { passed: true, steps: {} }
    }

    const steps: PreconditionResult['steps'] = {}

    // Step 2: Form-side preconditions — run all validation sets for this category
    const categorySets = await this.getSetsForTarget(target, category)

    if (categorySets.length > 0) {
      const categoryBuilder = new ValidationResultBuilder()
      const coveredNames = new Set<string>()

      for (const set of categorySets) {
        const validations = Array.isArray(set.data?.validations) ? set.data.validations : []
        const purpose = typeof set.data?.purpose === 'string' && set.data.purpose.length > 0
          ? set.data.purpose
          : category

        for (const ruleRef of validations) {
          const result = await this.run(ruleRef, target, {}, { purpose })
          mergeResult(categoryBuilder, result)
        }

        if (typeof set.data?.name === 'string') {
          coveredNames.add(set.data.name.toLowerCase())
        }
      }

      const categoryResult = categoryBuilder.toJSON()
      steps.formSide = { valid: categoryResult.valid, category, setsRun: categorySets.length }

      if (!categoryResult.valid) {
        return { passed: false, steps }
      }

      // Track which names are already covered so step 3 doesn't re-run them
      steps._coveredNames = coveredNames
    }

    // Step 3: Caller-side extras
    const requiredNames = callerRequiredNames === false
      ? []
      : (Array.isArray(callerRequiredNames) ? callerRequiredNames : ['basic'])

    if (requiredNames.length > 0) {
      const coveredNames = steps._coveredNames || new Set<string>()

      for (const name of requiredNames) {
        const lowerName = name.toLowerCase()

        if (coveredNames.has(lowerName)) {
          continue // already checked in step 2
        }

        // "basic" includes built-in validateDocumentData
        if (lowerName === 'basic') {
          // Fast path: trust document.valid if it's true
          if ((target as any).valid === true) {
            // Still check form-defined "basic" named sets
            const basicSets = await this.getSetsForTargetByName(target, 'basic')

            if (basicSets.length === 0) {
              continue // built-in passed (valid===true), no custom "basic" sets
            }

            // Run custom "basic" named sets
            const basicBuilder = new ValidationResultBuilder()
            for (const set of basicSets) {
              const validations = Array.isArray(set.data?.validations) ? set.data.validations : []
              for (const ruleRef of validations) {
                const result = await this.run(ruleRef, target, {})
                mergeResult(basicBuilder, result)
              }
            }

            const basicResult = basicBuilder.toJSON()
            if (!basicResult.valid) {
              steps.callerSide = { valid: false, failedName: 'basic' }
              return { passed: false, steps }
            }

            continue
          }

          // valid is false or undefined — run full basic validation
          const basic = await this.validateBasic(target)

          if (!basic.valid) {
            steps.callerSide = { valid: false, failedName: 'basic' }
            return { passed: false, steps }
          }

          // Also run form-defined "basic" named sets
          const basicSets = await this.getSetsForTargetByName(target, 'basic')
          for (const set of basicSets) {
            const validations = Array.isArray(set.data?.validations) ? set.data.validations : []
            for (const ruleRef of validations) {
              const result = await this.run(ruleRef, target, {})
              if (!result.valid) {
                steps.callerSide = { valid: false, failedName: 'basic' }
                return { passed: false, steps }
              }
            }
          }

          continue
        }

        // Non-basic named validation: find and run matching named sets
        const namedSets = await this.getSetsForTargetByName(target, name)

        if (namedSets.length === 0) {
          continue // no sets defined for this name — not a failure
        }

        const namedBuilder = new ValidationResultBuilder()
        for (const set of namedSets) {
          const validations = Array.isArray(set.data?.validations) ? set.data.validations : []
          for (const ruleRef of validations) {
            const result = await this.run(ruleRef, target, {})
            mergeResult(namedBuilder, result)
          }
        }

        const namedResult = namedBuilder.toJSON()
        if (!namedResult.valid) {
          steps.callerSide = { valid: false, failedName: name }
          return { passed: false, steps }
        }
      }
    }

    return { passed: true, steps }
  }

  private async _getRule(ruleIdentifier: Types.IdOrReference | Types.Document): Promise<ValidationRule | null> {
    if (isDocument(ruleIdentifier)) {
      return EditableDocument.isEditableDocument(ruleIdentifier)
        ? ruleIdentifier.cloneAsDocument() as ValidationRule
        : ruleIdentifier as ValidationRule
    }

    return this._aeppic.get(ruleIdentifier) as Promise<ValidationRule | null>
  }

  private async _getTarget(targetIdentifier: Types.IdOrReference | Types.Document): Promise<Types.Document | null> {
    if (isDocument(targetIdentifier)) {
      if (EditableDocument.isEditableDocument(targetIdentifier)) {
        const snapshot = targetIdentifier.cloneAsDocument()
        // Unsaved edits cannot inherit the certificate of their saved base revision.
        snapshot.stamps = targetIdentifier.isDirty ? [] : targetIdentifier.stamps
        return snapshot
      }
      return targetIdentifier
    }

    return this._aeppic.get(targetIdentifier)
  }
}

class ReadOnlyValidationAeppic {
  constructor(private _aeppic: AeppicInterface) {
    Object.freeze(this)
  }

  get Actions(): never {
    return throwReadOnlyError('Actions.run')
  }

  get Commands(): never {
    return throwReadOnlyError('Commands.execute')
  }

  save() { throwReadOnlyError('save') }
  saveAll() { throwReadOnlyError('saveAll') }
  delete() { throwReadOnlyError('delete') }
  deleteHard() { throwReadOnlyError('deleteHard') }
  move() { throwReadOnlyError('move') }
  change() { throwReadOnlyError('change') }
  stamp() { throwReadOnlyError('stamp') }
  upgrade() { throwReadOnlyError('upgrade') }
  applyWrites() { throwReadOnlyError('applyWrites') }
  commit() { throwReadOnlyError('commit') }

  get Query() { return this._aeppic.Query }
  get DateTime() { return this._aeppic.DateTime }
  get DateTimeDuration() { return this._aeppic.DateTimeDuration }
  get DateTimeInfo() { return (this._aeppic as any).DateTimeInfo }
  get DateTimeInterval() { return this._aeppic.DateTimeInterval }
  get Functions() { return (this._aeppic as any).Functions }
  get Parser() { return (this._aeppic as any).Parser }
  get Preferences() { return this._aeppic.Preferences }
  get Translator() { return this._aeppic.Translator }
  get Features() { return this._aeppic.Features }
  get Formatting() { return this._aeppic.Formatting }
  get Financial() { return (this._aeppic as any).Financial }
  get Warn() { return this._aeppic.Warn }
  get Log() { return this._aeppic.Log }

  asReference(...args: any[]) { return (this._aeppic as any).asReference(...args) }
  get(...args: any[]) { return (this._aeppic as any).get(...args) }
  getAll(...args: any[]) { return (this._aeppic as any).getAll(...args) }
  getDocumentForm(...args: any[]) { return (this._aeppic as any).getDocumentForm(...args) }
  getFormForDocument(...args: any[]) { return (this._aeppic as any).getFormForDocument(...args) }
  getForm(...args: any[]) { return (this._aeppic as any).getForm(...args) }
  getPreviousChanges(...args: any[]) { return (this._aeppic as any).getPreviousChanges(...args) }
  getFormVersions(...args: any[]) { return (this._aeppic as any).getFormVersions(...args) }
  hasChildren(...args: any[]) { return (this._aeppic as any).hasChildren(...args) }
  canUpgrade(...args: any[]) { return (this._aeppic as any).canUpgrade(...args) }
  uuid(...args: any[]) { return (this._aeppic as any).uuid(...args) }
  download(...args: any[]) { return (this._aeppic as any).download(...args) }
  hasRight(...args: any[]) { return (this._aeppic as any).hasRight(...args) }
  resolve(...args: any[]) { return (this._aeppic as any).resolve(...args) }
  resolveAll(...args: any[]) { return (this._aeppic as any).resolveAll(...args) }
  resolveEdit(...args: any[]) { return (this._aeppic as any).resolveEdit(...args) }
  resolveToField(...args: any[]) { return (this._aeppic as any).resolveToField(...args) }
  find(...args: any[]) { return (this._aeppic as any).find(...args) }
  findOne(...args: any[]) { return (this._aeppic as any).findOne(...args) }
  match(...args: any[]) { return (this._aeppic as any).match(...args) }
  matchAll(...args: any[]) { return (this._aeppic as any).matchAll(...args) }
  edit(...args: any[]) { return (this._aeppic as any).edit(...args) }
  editAll(...args: any[]) { return (this._aeppic as any).editAll(...args) }
  translate(...args: any[]) { return (this._aeppic as any).translate(...args) }
  getAccountForProfilePage(...args: any[]) { return (this._aeppic as any).getAccountForProfilePage(...args) }
  getProfilePageForAccount(...args: any[]) { return (this._aeppic as any).getProfilePageForAccount(...args) }
  fetch(...args: any[]) { return (this._aeppic as any).fetch(...args) }
}

function getActionRefs(rule: ValidationRule): (Types.IdOrReference | Types.Document)[] {
  if (Array.isArray(rule.data?.actions) && rule.data.actions.length > 0) {
    return rule.data.actions
  }

  if (rule.data?.action && rule.data.action.id) {
    return [rule.data.action]
  }

  return []
}

function copyValidationData(value: any): any {
  if (Array.isArray(value)) return value.map(copyValidationData)
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, copyValidationData(item)]))
  }
  return value
}

function isNotApplicableResult(value: any): boolean {
  return value?.status === 'NOT_APPLICABLE' || value?.status === 'not_applicable'
}

function applyActionResult(actionResult: ActionResult, builder: ValidationResultBuilder) {
  if (!actionResult.ok) {
    builder.invalid(actionResult.error || 'Validation action failed')
    return
  }

  const value = actionResult.value

  if (isNotApplicableResult(value)) {
    builder.notApplicable()
    return
  }

  if (value === true || value === undefined || value === null) {
    return
  }

  if (value === false) {
    builder.invalid()
    return
  }

  if (isValidationResultLike(value)) {
    mergeResult(builder, value)
  }
}

function mergeResult(builder: ValidationResultBuilder, result: ValidationResult) {
  if (result.status === 'not_applicable') builder.invalid({ code: 'VALIDATION_NOT_APPLICABLE', message: 'A required validation does not apply to this form version' })
  for (const warning of result.warnings ?? []) {
    builder.warning(warning)
  }

  for (const error of result.errors ?? []) {
    builder.invalid(error)
  }

  for (const evidence of result.evidence ?? []) {
    builder.evidence(evidence)
  }

  if (result.valid === false || result.status === 'invalid') {
    builder.invalid()
  }
}

function isValidationResultLike(value: any): value is ValidationResult {
  return value &&
    typeof value === 'object' &&
    typeof value.valid === 'boolean' &&
    Array.isArray(value.warnings) &&
    Array.isArray(value.errors) &&
    Array.isArray(value.evidence)
}

type FieldValidationContext = {
  aeppic: AeppicInterface
  target: Types.Document
  field: FieldInfo
  fieldName: string
  value: any
}

async function applyBuiltInFieldValidation(builder: ValidationResultBuilder, validation: string, context: FieldValidationContext) {
  switch (validation) {
    case 'unique':
      await applyUniqueFieldValidation(builder, context)
      return
    default:
      builder.invalid({
        code: 'UNKNOWN_FIELD_VALIDATION',
        message: `Unknown field validation "${validation}"`,
        field: context.fieldName,
      })
  }
}

async function applyUniqueFieldValidation(builder: ValidationResultBuilder, context: FieldValidationContext) {
  const values = Array.isArray(context.value) ? context.value : null

  if (!values) {
    return
  }

  const selector = getFieldTagString(context.field, 'unique') || null
  const seen = new Map<string, number>()

  for (let index = 0; index < values.length; index += 1) {
    const value = values[index]
    const key = await getUniqueFieldValueKey(context, value, selector)

    if (!key) {
      continue
    }

    const existingIndex = seen.get(key)

    if (existingIndex === undefined) {
      seen.set(key, index)
      continue
    }

    builder.invalid({
      code: 'UNIQUE_FIELD_DUPLICATE',
      message: `${context.fieldName} contains a duplicate value`,
      field: context.fieldName,
      path: [context.fieldName, index],
      duplicateOf: [context.fieldName, existingIndex],
      value: key,
    })
  }
}

async function getUniqueFieldValueKey(context: FieldValidationContext, value: any, selector: string | null): Promise<string> {
  if (value === null || value === undefined) {
    return ''
  }

  if (selector) {
    return getUniqueSelectorValueKey(await resolveUniqueSelectorSource(context, value, selector), selector)
  }

  const { field } = context

  switch (field.type) {
    case 'string':
    case 'number':
    case 'boolean':
      return `${field.type}:${String(value)}`
    case 'object':
      return getUniqueObjectFieldValueKey(field.subType, value)
    default:
      return stableStringify(value)
  }
}

async function resolveUniqueSelectorSource(context: FieldValidationContext, value: any, selector: string) {
  if (selector === 'id') {
    return readPath(value, 'id')
  }

  if (selector === 'id|v') {
    return [readPath(value, 'id'), readPath(value, 'v')]
  }

  if (isReference(value)) {
    const document = await context.aeppic.get(value.id)
    return readPath(document, selector)
  }

  return readPath(value, selector)
}

function getUniqueSelectorValueKey(value: any, selector: string): string {
  if (value === null || value === undefined || value === '') {
    return ''
  }

  if (Array.isArray(value)) {
    if (value.every(item => item === null || item === undefined || item === '')) {
      return ''
    }
  }

  return `${selector}:${stableStringify(value)}`
}

function getUniqueObjectFieldValueKey(subType: string, value: any): string {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return ''
  }

  switch (subType) {
    case 'ref':
      return value.id ? `ref:${value.id}` : ''
    case 'image':
      return value.dataUrl ? `image:${value.dataUrl}` : ''
    case 'file':
      if (value.sha1) {
        return `file-sha1:${value.sha1}`
      }

      if (value.dataUrl) {
        return `file-url:${value.dataUrl}`
      }

      return stableStringify({
        name: value.name,
        size: value.size,
        type: value.type,
      })
    case 'geolocation':
      return typeof value.lat === 'number' && typeof value.lon === 'number'
        ? `geolocation:${value.lat},${value.lon}`
        : ''
    case 'currency':
      return stableStringify({
        amount: value.amount,
        currency: value.currency,
        precision: value.precision,
      })
    case 'duration':
    case 'address':
    case 'object':
      return stableStringify(value)
    default:
      return stableStringify(value)
  }
}

function stableStringify(value: any): string {
  if (value === null || value === undefined) {
    return ''
  }

  if (typeof value !== 'object') {
    return `${typeof value}:${String(value)}`
  }

  if (Array.isArray(value)) {
    return `[${value.map(item => stableStringify(item)).join(',')}]`
  }

  const entries = Object.keys(value)
    .sort()
    .map(key => `${JSON.stringify(key)}:${stableStringify(value[key])}`)

  return `{${entries.join(',')}}`
}

function getFieldTagString(field: FieldInfo, name: string): string {
  const tag = field?.tags?.[name]

  if (!tag || tag === true) {
    return ''
  }

  if (typeof tag === 'string') {
    return tag.trim()
  }

  if (typeof tag === 'object' && 'value' in tag && typeof tag.value === 'string') {
    return tag.value.trim()
  }

  return ''
}

function readPath(value: any, path: string): any {
  if (!path) {
    return value
  }

  return path
    .split('.')
    .filter(part => part.length > 0)
    .reduce((current, part) => current == null ? undefined : current[part], value)
}

function toMessage(args: any[]): ValidationMessage {
  if (args.length === 1) {
    const [message] = args

    if (typeof message === 'string') {
      return { message }
    }

    if (message && typeof message === 'object') {
      return { message: String(message.message ?? message.code ?? 'Validation message'), ...message }
    }
  }

  if (args.length >= 2 && args[0] && typeof args[0] === 'object') {
    const [meta, template, ...values] = args
    const message = format(String(template), values)
    return { message, ...meta }
  }

  const [template, ...values] = args
  return { message: format(String(template ?? 'Validation message'), values) }
}

function format(template: string, values: any[]) {
  let index = 0
  return template.replace(/%s/g, () => String(values[index++]))
}

function throwReadOnlyError(method: string): never {
  throw new Error(`Validation actions cannot write or execute write-capable APIs (${method})`)
}

function isDocument(value: any): value is Types.Document {
  return Boolean(value?.id && value?.f && value?.data)
}
